import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import './MortgageCalculatorPage.css';

const fmt  = v => '€ ' + Math.round(v).toLocaleString();
const fmtK = v => v >= 1000 ? '€ ' + (v/1000).toFixed(0) + 'k' : '€ ' + v;

/* ── Draggable Slider ── */
function Slider({ label, value, min, max, step, onChange, display, hint, info }) {
  const trackRef = useRef(null);
  const dragging = useRef(false);
  const calcValue = useCallback((clientX) => {
    const rect = trackRef.current.getBoundingClientRect();
    const pct  = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const raw  = min + pct * (max - min);
    const snapped = Math.round(raw / step) * step;
    return Math.max(min, Math.min(max, snapped));
  }, [min, max, step]);
  const rafId = useRef(null);
  const onMouseDown = e => {
    e.preventDefault(); dragging.current = true; onChange(calcValue(e.clientX));
    const move = e2 => {
      if (!dragging.current) return;
      if (rafId.current) cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => onChange(calcValue(e2.clientX)));
    };
    const up = () => { dragging.current = false; window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
    window.addEventListener('mousemove', move); window.addEventListener('mouseup', up);
  };
  const onTouchStart = e => {
    dragging.current = true; onChange(calcValue(e.touches[0].clientX));
    const move = e2 => {
      if (!dragging.current) return;
      if (rafId.current) cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => onChange(calcValue(e2.touches[0].clientX)));
    };
    const end = () => { dragging.current = false; window.removeEventListener('touchmove', move); window.removeEventListener('touchend', end); };
    window.addEventListener('touchmove', move, { passive: true }); window.addEventListener('touchend', end);
  };
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="sl-row">
      <div className="sl-top">
        <div className="sl-label-wrap">
          <span className="sl-label">{label}</span>
          {info && <span className="sl-info" title={info}>?</span>}
        </div>
        <span className="sl-value">{display}</span>
      </div>
      <div className="sl-track-wrap" ref={trackRef} onMouseDown={onMouseDown} onTouchStart={onTouchStart}>
        <div className="sl-track"><div className="sl-fill" style={{ width: pct + '%' }} /></div>
        <div className="sl-thumb" style={{ left: pct + '%' }} />
      </div>
      {hint && <div className="sl-hint">{hint}</div>}
    </div>
  );
}

/* ── Mortgage Tab ── */
function MortgageTab() {
  const { t } = useLanguage();
  const [price,   setPrice]   = useState(350000);
  const [depPct,  setDepPct]  = useState(20);
  const [rate,    setRate]    = useState(3.5);
  const [years,   setYears]   = useState(25);
  const [hovered, setHovered] = useState(null);
  const deposit   = Math.round((depPct / 100) * price);
  const loan      = Math.max(0, price - deposit);
  const monthRate = rate / 100 / 12;
  const n         = years * 12;
  const monthly   = loan > 0 && monthRate > 0 ? (loan * monthRate * Math.pow(1 + monthRate, n)) / (Math.pow(1 + monthRate, n) - 1) : loan / n;
  const totalPaid = monthly * n;
  const totalInt  = totalPaid - loan;
  const R = 60;
  const circumference = 2 * Math.PI * R;
  const prinDash  = (loan / totalPaid) * circumference;
  const intDash   = (totalInt / totalPaid) * circumference;
  const startOffset = circumference * 0.25;
  const chartData = [];
  let rem = loan;
  for (let y = 1; y <= years && rem > 0; y++) {
    let yi = 0, yp = 0;
    for (let m = 0; m < 12 && rem > 0; m++) {
      const ip = rem * monthRate; const pp = Math.min(monthly - ip, rem);
      yi += ip; yp += pp; rem -= pp;
    }
    chartData.push({ year: y, interest: yi, principal: yp, total: yi + yp });
  }
  const maxTotal = Math.max(...chartData.map(d => d.total));
  return (
    <div className="mc-grid">
      <div className="mc-panel">
        <div className="mc-panel__title">{t('mortgage.yourDetails')}</div>
        <Slider label={t('mortgage.propPrice')} value={price} min={50000} max={2000000} step={5000} onChange={setPrice} display={fmt(price)} />
        <Slider label={t('mortgage.deposit')} value={depPct} min={0} max={50} step={1} onChange={setDepPct} display={`${depPct}% · ${fmt(deposit)}`} hint={`${t('mortgage.loanAmtHint')} ${fmt(loan)}`} />
        <Slider label={t('mortgage.interestRate')} value={rate} min={1} max={10} step={0.1} onChange={setRate} display={rate.toFixed(1) + '%'} info={t('mortgage.interestRateInfo')} hint={t('mortgage.rateCaption')} />
        <Slider label={t('mortgage.mortgageTerm')} value={years} min={5} max={35} step={1} onChange={setYears} display={years + ' ' + t('mortgage.years')} />
        <div className="mc-stats">
          <div className="mc-stat mc-stat--main">
            <div className="mc-stat__label">{t('mortgage.monthlyPayment')}</div>
            <div className="mc-stat__value">{fmt(monthly)}</div>
            <div className="mc-stat__sub">{t('mortgage.perMonthFor')} {years} {t('mortgage.years')}</div>
          </div>
          <div className="mc-stat"><div className="mc-stat__label">{t('mortgage.loanAmount')}</div><div className="mc-stat__value">{fmt(loan)}</div></div>
          <div className="mc-stat"><div className="mc-stat__label">{t('mortgage.totalInterest')}</div><div className="mc-stat__value mc-stat__value--red">{fmt(totalInt)}</div></div>
          <div className="mc-stat"><div className="mc-stat__label">{t('mortgage.totalRepayment')}</div><div className="mc-stat__value">{fmt(totalPaid)}</div></div>
        </div>
      </div>
      <div className="mc-panel">
        <div className="mc-panel__title">{t('mortgage.repaymentBreakdown')}</div>
        <div className="mc-donut-wrap">
          <svg viewBox="0 0 160 160" className="mc-donut">
            <circle cx="80" cy="80" r="60" fill="none" stroke="#EFEEE9" strokeWidth="22" />
            <circle cx="80" cy="80" r="60" fill="none" stroke="#0E0E10" strokeWidth="22" strokeDasharray={`${prinDash} ${circumference - prinDash}`} strokeDashoffset={startOffset} strokeLinecap="butt" />
            <circle cx="80" cy="80" r="60" fill="none" stroke="#EF4444" strokeWidth="22" strokeDasharray={`${intDash} ${circumference - intDash}`} strokeDashoffset={startOffset - prinDash} strokeLinecap="butt" opacity="0.85" />
            <text x="80" y="73" textAnchor="middle" fontSize="10" fill="#9CA3AF" fontFamily="var(--font-body)">{t('mortgage.monthlyLabel')}</text>
            <text x="80" y="91" textAnchor="middle" fontSize="20" fontWeight="500" fill="#1F2937" fontFamily="var(--font-body)">{Math.round(monthly).toLocaleString()}</text>
            <text x="80" y="103" textAnchor="middle" fontSize="9" fill="#9CA3AF" fontFamily="var(--font-body)">{t('mortgage.perMonth')}</text>
          </svg>
          <div className="mc-donut-legend">
            <div className="mc-legend-item"><span style={{background:'#0E0E10'}}/>{t('mortgage.principal')} — {Math.round((loan/totalPaid)*100)}%</div>
            <div className="mc-legend-item"><span style={{background:'#EF4444', opacity:0.82}}/>{t('mortgage.interest')} — {Math.round((totalInt/totalPaid)*100)}%</div>
          </div>
        </div>
        <div className="mc-panel__title" style={{marginTop:20}}>{t('mortgage.yearByYear')}</div>
        <div className="mc-chart">
          <div className="mc-chart__bars">
            {chartData.map((d, i) => {
              const isHov = hovered === i;
              const prinH = (d.principal / maxTotal) * 100;
              const intH  = (d.interest  / maxTotal) * 100;
              return (
                <div key={d.year} className={`mc-chart__col ${isHov ? 'mc-chart__col--hov' : ''}`} onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}>
                  {isHov && (
                    <div className="mc-chart__tooltip">
                      <div className="mc-chart__tt-year">{t('mortgage.year')} {d.year}</div>
                      <div className="mc-chart__tt-row"><span style={{color:'#0E0E10'}}>●</span> {t('mortgage.principal')}: {fmt(d.principal)}</div>
                      <div className="mc-chart__tt-row"><span style={{color:'#EF4444'}}>●</span> {t('mortgage.interest')}: {fmt(d.interest)}</div>
                    </div>
                  )}
                  <div className="mc-chart__stack">
                    <div className="mc-chart__seg mc-chart__seg--int"  style={{height: intH  + '%'}} />
                    <div className="mc-chart__seg mc-chart__seg--prin" style={{height: prinH + '%'}} />
                  </div>
                  <div className="mc-chart__label">{(d.year === 1 || d.year % 5 === 0 || d.year === chartData[chartData.length-1].year) ? d.year : ''}</div>
                </div>
              );
            })}
          </div>
          <div className="mc-chart__legend">
            <span><i style={{background:'#0E0E10'}}/> {t('mortgage.principal')}</span>
            <span><i style={{background:'#EF4444', opacity:0.82}}/> {t('mortgage.interest')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Affordability Tab ── */
function AffordabilityTab() {
  const { t } = useLanguage();
  const [income,   setIncome]   = useState(4000);
  const [expenses, setExpenses] = useState(1200);
  const [deposit,  setDeposit]  = useState(40000);
  const [rate,     setRate]     = useState(3.5);
  const [years,    setYears]    = useState(25);
  const disposable = income - expenses;
  const maxMonthly = disposable * 0.35;
  const monthRate  = rate / 100 / 12;
  const n          = years * 12;
  const maxLoan    = maxMonthly > 0 && monthRate > 0 ? maxMonthly * ((Math.pow(1 + monthRate, n) - 1) / (monthRate * Math.pow(1 + monthRate, n))) : 0;
  const maxPrice   = maxLoan + deposit;
  const dtiRatio   = income > 0 ? ((maxMonthly / income) * 100).toFixed(0) : 0;
  const affordable = maxPrice > 0;
  return (
    <div className="mc-grid">
      <div className="mc-panel">
        <div className="mc-panel__title">{t('mortgage.yourFinances')}</div>
        <Slider label={t('mortgage.monthlyIncome')} value={income} min={500} max={15000} step={100} onChange={setIncome} display={fmt(income) + '/mo'} info={t('mortgage.monthlyIncomeInfo')} />
        <Slider label={t('mortgage.monthlyExpenses')} value={expenses} min={0} max={10000} step={100} onChange={v => setExpenses(v)} display={fmt(expenses) + '/mo'} hint={`${t('mortgage.disposableIncome')} ${fmt(Math.max(0, disposable))}/mo`} info={t('mortgage.monthlyExpensesInfo')} />
        <Slider label={t('mortgage.availableDeposit')} value={deposit} min={0} max={500000} step={5000} onChange={setDeposit} display={fmt(deposit)} />
        <Slider label={t('mortgage.interestRate')} value={rate} min={1} max={10} step={0.1} onChange={setRate} display={rate.toFixed(1) + '%'} info={t('mortgage.interestRateInfo')} hint={t('mortgage.rateCaption')} />
        <Slider label={t('mortgage.mortgageTerm')} value={years} min={5} max={35} step={1} onChange={setYears} display={years + ' ' + t('mortgage.years')} />
      </div>
      <div className="mc-panel">
        <div className="mc-panel__title">{t('mortgage.whatCanAfford')}</div>
        {affordable ? (
          <>
            <div className="mc-afford-main">
              <div className="mc-afford-label">{t('mortgage.maxPropPrice')}</div>
              <div className="mc-afford-value">{fmt(maxPrice)}</div>
              <div className="mc-afford-sub">{t('mortgage.basedOnDTI').replace('{pct}', dtiRatio)}</div>
              <div className="mc-afford-sub" style={{marginTop:6, fontStyle:'italic', color:'#6B7280'}}>
                {t('mortgage.dtiGuidelineNote')}
              </div>
            </div>
            <div className="mc-stats" style={{marginTop:16}}>
              <div className="mc-stat mc-stat--main"><div className="mc-stat__label">{t('mortgage.maxMonthly')}</div><div className="mc-stat__value">{fmt(maxMonthly)}</div><div className="mc-stat__sub">{t('mortgage.pctOfDisposable')}</div></div>
              <div className="mc-stat"><div className="mc-stat__label">{t('mortgage.maxLoan')}</div><div className="mc-stat__value">{fmt(maxLoan)}</div></div>
              <div className="mc-stat"><div className="mc-stat__label">{t('mortgage.yourDeposit')}</div><div className="mc-stat__value">{fmt(deposit)}</div></div>
              <div className="mc-stat"><div className="mc-stat__label">{t('mortgage.depositPct')}</div><div className="mc-stat__value">{maxPrice > 0 ? Math.round((deposit/maxPrice)*100) : 0}%</div></div>
            </div>
            <div style={{marginTop:20}}>
              <div className="mc-panel__title">{t('mortgage.incomeAlloc')}</div>
              <div className="mc-afford-bar-wrap">
                {[
                  { label: t('mortgage.mortgageLabel'), pct: (maxMonthly/income)*100, color: '#0E0E10' },
                  { label: t('mortgage.expensesLabel'), pct: (expenses/income)*100,   color: '#EF4444' },
                  { label: t('mortgage.remainingLabel'), pct: Math.max(0, 100 - (maxMonthly/income)*100 - (expenses/income)*100), color: '#10B981' },
                ].map(b => (
                  <div key={b.label} style={{marginBottom:10}}>
                    <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                      <span style={{fontSize:12,color:'#6B7280'}}>{b.label}</span>
                      <span style={{fontSize:12,fontWeight:600,color:b.color}}>{Math.round(b.pct)}%</span>
                    </div>
                    <div style={{height:8,background:'#F3F4F6',borderRadius:4,overflow:'hidden'}}>
                      <div style={{height:'100%',width:b.pct+'%',background:b.color,borderRadius:4,transition:'width 0.3s'}}/>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mc-afford-note">{t('mortgage.affordNote')}</div>
          </>
        ) : (
          <div className="mc-afford-empty">{t('mortgage.adjustToSee')}</div>
        )}
      </div>
    </div>
  );
}

/* ── Overpayment Tab ── */
function OverpaymentTab() {
  const { t } = useLanguage();
  const [loan,    setLoan]    = useState(280000);
  const [rate,    setRate]    = useState(3.5);
  const [years,   setYears]   = useState(25);
  const [extra,   setExtra]   = useState(200);
  const [hovered, setHovered] = useState(null);
  const monthRate   = rate / 100 / 12;
  const n           = years * 12;
  const baseMonthly = loan > 0 && monthRate > 0 ? (loan * monthRate * Math.pow(1 + monthRate, n)) / (Math.pow(1 + monthRate, n) - 1) : loan / n;
  const simulate = (extraPayment) => {
    const data = []; let rem = loan, month = 0;
    while (rem > 0.01 && month < n + 12) {
      const ip = rem * monthRate; const pp = Math.min(baseMonthly - ip + extraPayment, rem);
      rem -= pp; month++;
      if (month % 12 === 0 || rem <= 0.01) data.push({ year: Math.ceil(month/12), remaining: Math.max(0, rem) });
    }
    return { months: month, totalPaid: (baseMonthly + extraPayment) * month - Math.max(0, rem), data };
  };
  const base = simulate(0); const over = simulate(extra);
  const monthsSaved   = base.months - over.months;
  const yearsSaved    = Math.floor(monthsSaved / 12);
  const mthsSaved     = monthsSaved % 12;
  const interestSaved = (base.totalPaid - loan) - (over.totalPaid - loan);
  const maxYears = Math.ceil(base.months / 12);
  const baseMap  = Object.fromEntries(base.data.map(d => [d.year, d.remaining]));
  const overMap  = Object.fromEntries(over.data.map(d => [d.year, d.remaining]));
  return (
    <div className="mc-grid">
      <div className="mc-panel">
        <div className="mc-panel__title">{t('mortgage.yourMortgage')}</div>
        <Slider label={t('mortgage.loanAmount2')} value={loan} min={10000} max={1500000} step={5000} onChange={setLoan} display={fmt(loan)} />
        <Slider label={t('mortgage.interestRate')} value={rate} min={1} max={10} step={0.1} onChange={setRate} display={rate.toFixed(1) + '%'} info={t('mortgage.interestRateInfo')} hint={t('mortgage.rateCaption')} />
        <Slider label={t('mortgage.mortgageTerm')} value={years} min={5} max={35} step={1} onChange={setYears} display={years + ' ' + t('mortgage.years')} />
        <Slider label={t('mortgage.extraMonthly')} value={extra} min={0} max={2000} step={50} onChange={setExtra} display={fmt(extra) + '/mo'} info={t('mortgage.extraMonthlyInfo')} />
        <div className="mc-stats" style={{marginTop:8}}>
          <div className="mc-stat"><div className="mc-stat__label">{t('mortgage.standardMonthly')}</div><div className="mc-stat__value">{fmt(baseMonthly)}</div></div>
          <div className="mc-stat mc-stat--main"><div className="mc-stat__label">{t('mortgage.withOverpayment')}</div><div className="mc-stat__value">{fmt(baseMonthly + extra)}</div></div>
        </div>
      </div>
      <div className="mc-panel">
        <div className="mc-panel__title">{t('mortgage.whatOverpaySaves')}</div>
        <div className="mc-over-savings">
          <div className="mc-over-saving">
            <div className="mc-over-saving__icon" style={{background:'#EEF2FF'}}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0E0E10" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            </div>
            <div>
              <div className="mc-over-saving__label">{t('mortgage.timeSaved')}</div>
              <div className="mc-over-saving__value">{yearsSaved > 0 ? `${yearsSaved}y ` : ''}{mthsSaved > 0 ? `${mthsSaved}mo` : yearsSaved === 0 ? t('mortgage.noSaving') : ''}</div>
              <div className="mc-over-saving__sub">{t('mortgage.mortgageEnds')} {over.months > 0 ? Math.ceil(over.months/12) : years} {t('mortgage.years')}</div>
            </div>
          </div>
          <div className="mc-over-saving">
            <div className="mc-over-saving__icon" style={{background:'#FEF3C7'}}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
            </div>
            <div>
              <div className="mc-over-saving__label">{t('mortgage.interestSaved')}</div>
              <div className="mc-over-saving__value" style={{color:'#10B981'}}>{fmt(Math.max(0, interestSaved))}</div>
              <div className="mc-over-saving__sub">{t('mortgage.lessPaidToBank')}</div>
            </div>
          </div>
        </div>
        <div className="mc-panel__title" style={{marginTop:20}}>{t('mortgage.remainingBalance')}</div>
        <div className="mc-balance-chart">
          <svg viewBox={`0 0 ${maxYears * 10 + 20} 100`} preserveAspectRatio="none" className="mc-balance-svg">
            <polyline points={Array.from({length:maxYears},(_,i) => { const yr=i+1; const r=baseMap[yr]!==undefined?baseMap[yr]:0; return `${10+i*10},${100-(r/loan)*95}`; }).join(' ')} fill="none" stroke="#B8924A" strokeWidth="2" strokeLinejoin="round"/>
            <polyline points={Array.from({length:maxYears},(_,i) => { const yr=i+1; const r=overMap[yr]!==undefined?overMap[yr]:0; return `${10+i*10},${100-(r/loan)*95}`; }).join(' ')} fill="none" stroke="#0E0E10" strokeWidth="2.5" strokeLinejoin="round"/>
          </svg>
          <div className="mc-balance-legend">
            <span><i style={{background:'#B8924A'}}/> {t('mortgage.standard')}</span>
            <span><i style={{background:'#0E0E10'}}/> {t('mortgage.withOverpaymentLegend')}</span>
          </div>
        </div>
        <div className="mc-afford-note">{t('mortgage.overpayNote')}</div>
      </div>
    </div>
  );
}

/* ── Main Page ── */
export default function MortgageCalculatorPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [tab, setTab] = useState('mortgage');

  const tabs = [
    { id: 'mortgage',      label: t('mortgage.tabMortgage') },
    // Affordability tab hidden — uncomment the line below to re-enable.
    // { id: 'affordability', label: t('mortgage.tabAffordability') },
    { id: 'overpayment',   label: t('mortgage.tabOverpayment') },
  ];

  return (
    <div className="mc-page">
      <header className="toolHero">
        <div className="toolHero__topBar">
          <button type="button" className="toolHero__back" onClick={() => navigate(-1)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            {t('mortgage.back')}
          </button>
        </div>
        <div className="toolHero__inner">
          <span className="toolHero__eyebrow">CYPRUS PROPERTY TOOL</span>
          <h1 className="toolHero__title">{t('mortgage.heroTitle')}</h1>
          <p className="toolHero__subtitle">{t('mortgage.sub')}</p>
        </div>
      </header>
      <div className="mc-page__body">
        <div className="mc-tabs">
          {tabs.map(tabItem => (
            <button key={tabItem.id} className={`mc-tab ${tab === tabItem.id ? 'mc-tab--active' : ''}`} onClick={() => setTab(tabItem.id)}>{tabItem.label}</button>
          ))}
        </div>
        {tab === 'mortgage'      && <MortgageTab />}
        {tab === 'affordability' && <AffordabilityTab />}
        {tab === 'overpayment'   && <OverpaymentTab />}
        <div style={{textAlign:'center', fontSize:12, color:'#6B7280', margin:'24px auto 0', maxWidth:760, padding:'12px 16px', lineHeight:1.55, background:'#F9FAFB', border:'1px solid #E5E7EB', borderRadius:8}}>
          {t('mortgage.purchaseCostsDisclaimer')}
        </div>
        <div className="mc-page__cta">
          <div className="mc-page__cta-text">
            <h3>{t('mortgage.ctaTitle')}</h3>
            <p>{t('mortgage.ctaSub')}</p>
          </div>
          <div className="mc-page__cta-btns">
            <button className="mc-page__cta-btn mc-page__cta-btn--primary" onClick={() => navigate('/listings')}>{t('mortgage.browseProperties')}</button>
            <button className="mc-page__cta-btn" onClick={() => navigate('/valuation')}>{t('mortgage.freeValuation')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
